import React, { useState, useEffect, useRef } from 'react';
import { Navigate, Link } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import styled from 'styled-components';
import {
  FiUser, FiLock, FiMail,
  FiShield, FiSave, FiRotateCcw, FiAlertCircle, FiServer, FiPlus,
  FiEdit2, FiCamera, FiUsers, FiFlag,
} from 'react-icons/fi';

import WebsiteLayout from '../../../layout/website/WebsiteLayout';
import noImage from '../../../../assets/images/no_img.jpg';
import { resolveAvatar, AVATARS } from '../../../../utilities/avatar';
import { COUNTRIES } from '../../../../data/countries';
import { updateUserPassword, updateUserProfile, updateUserImage } from '../../../../store/users/usersSlice';
import { getMyServers } from '../../../../store/servers/serversSlice';
import { setUser } from '../../../../store/auth/authSlice';
import { backendUrl } from '../../../../utilities/axios';

/* ── Wrappers ─────────────────────────────── */
const Page = styled.div`
  padding: 28px 24px;
  max-width: 860px;
  margin: 0 auto;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 18px;
  margin-bottom: 28px;
  padding: 20px 24px;
  background: #1A1A1A;
  border: 1px solid rgba(255,255,255,0.06);
  border-radius: 12px;
`;

const AvatarWrap = styled.div`
  position: relative;
  width: 72px;
  height: 72px;
  flex-shrink: 0;
`;

const AvatarImg = styled.div`
  width: 72px;
  height: 72px;
  border-radius: 14px;
  overflow: hidden;
  border: 2px solid ${p => p.gender === 'female' ? 'rgba(236,72,153,0.5)' : 'rgba(147,51,234,0.45)'};
  img { width: 100%; height: 100%; object-fit: cover; display: block; }
`;

const GenderAvatarIcon = styled.div`
  width: 72px;
  height: 72px;
  border-radius: 14px;
  overflow: hidden;
  border: 2px solid ${p => p.gender === 'female' ? 'rgba(236,72,153,0.5)' : 'rgba(147,51,234,0.45)'};
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${p => p.gender === 'female'
    ? 'linear-gradient(135deg, rgba(236,72,153,0.2), rgba(219,39,119,0.1))'
    : 'linear-gradient(135deg, rgba(147,51,234,0.2), rgba(99,102,241,0.1))'};
  color: ${p => p.gender === 'female' ? '#f472b6' : '#a78bfa'};
  font-size: 32px;
`;

const CameraBtn = styled.button`
  position: absolute;
  bottom: -5px;
  right: -5px;
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background: #7C3AED;
  border: 2px solid #1A1A1A;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 11px;
  transition: background 0.15s;
  &:hover { background: #6d28d9; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const UserMeta = styled.div`
  .u-name { font-size: 18px; font-weight: 700; color: #e2e8f0; }
  .u-role {
    display: inline-flex; align-items: center; gap: 5px;
    margin-top: 4px; font-size: 12px; color: #a78bfa;
    background: rgba(147,51,234,0.12); border: 1px solid rgba(147,51,234,0.22);
    border-radius: 20px; padding: 2px 10px;
  }
`;

/* ── Tabs ─────────────────────────────────── */
const Tabs = styled.div`
  display: flex;
  gap: 6px;
  margin-bottom: 20px;
  border-bottom: 1px solid rgba(255,255,255,0.06);
  padding-bottom: 0;
`;

const Tab = styled.button`
  display: flex;
  align-items: center;
  gap: 7px;
  padding: 10px 18px;
  border: none;
  background: none;
  font-family: inherit;
  font-size: 13.5px;
  font-weight: 600;
  color: ${p => p.active ? '#a78bfa' : '#6b7280'};
  border-bottom: 2px solid ${p => p.active ? '#9333EA' : 'transparent'};
  cursor: pointer;
  transition: color 0.15s, border-color 0.15s;
  margin-bottom: -1px;
  &:hover { color: #c4b5fd; }
  svg { font-size: 15px; }
`;

/* ── Card ─────────────────────────────────── */
const Card = styled.div`
  background: #1A1A1A;
  border: 1px solid rgba(255,255,255,0.06);
  border-radius: 12px;
  padding: 24px;
`;

/* ── Info row ─────────────────────────────── */
const InfoRow = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 0;
  border-bottom: 1px solid rgba(255,255,255,0.05);
  &:last-child { border-bottom: none; }

  .icon {
    width: 36px; height: 36px; border-radius: 9px; flex-shrink: 0;
    background: rgba(147,51,234,0.1); border: 1px solid rgba(147,51,234,0.2);
    display: flex; align-items: center; justify-content: center;
    svg { color: #a78bfa; font-size: 15px; }
  }
  .label { font-size: 11.5px; color: #4b5563; margin-bottom: 2px; }
  .value { font-size: 14px; color: #e2e8f0; font-weight: 500; }
`;

/* ── Form ─────────────────────────────────── */
const FormGroup = styled.div`
  margin-bottom: 20px;

  label {
    display: block; font-size: 12.5px; font-weight: 600;
    color: #9ca3af; margin-bottom: 7px; text-transform: uppercase;
    letter-spacing: 0.04em;
  }

  input, select {
    width: 100%; background: #111; border: 1px solid rgba(255,255,255,0.08);
    border-radius: 9px; padding: 11px 14px; color: #e2e8f0; font-size: 14px;
    font-family: inherit; outline: none; appearance: none;
    transition: border-color 0.2s;
    &::placeholder { color: #374151; }
    &:focus { border-color: rgba(147,51,234,0.5); background: #151515; }
    option { background: #1A1A1A; }
  }

  .err { font-size: 12px; color: #f87171; margin-top: 5px; }
`;

const EditDivider = styled.div`
  border-top: 1px solid rgba(255,255,255,0.06);
  margin: 16px 0 20px;
  display: flex;
  align-items: center;
  gap: 10px;
  padding-top: 16px;

  span {
    font-size: 13px;
    font-weight: 600;
    color: #6b7280;
    white-space: nowrap;
  }
  &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: rgba(255,255,255,0.05);
  }
`;

const BtnRow = styled.div`
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  margin-top: 24px;
`;

const Btn = styled.button`
  display: flex; align-items: center; gap: 7px;
  padding: 9px 20px; border-radius: 9px; border: none;
  font-family: inherit; font-size: 13.5px; font-weight: 600;
  cursor: pointer; transition: opacity 0.15s;
  opacity: ${p => p.disabled ? 0.55 : 1};
  background: ${p => p.variant === 'reset' ? 'rgba(239,68,68,0.15)' : p.variant === 'ghost' ? 'rgba(147,51,234,0.12)' : '#7C3AED'};
  color: ${p => p.variant === 'reset' ? '#f87171' : p.variant === 'ghost' ? '#a78bfa' : '#fff'};
  border: 1px solid ${p => p.variant === 'reset' ? 'rgba(239,68,68,0.3)' : p.variant === 'ghost' ? 'rgba(147,51,234,0.3)' : 'transparent'};
  &:hover { opacity: 0.85; }
`;

const AlertBox = styled.div`
  display: flex; align-items: flex-start; gap: 10px;
  background: rgba(239,68,68,0.08); border: 1px solid rgba(239,68,68,0.25);
  border-radius: 9px; padding: 12px 16px; margin-bottom: 20px;
  svg { color: #f87171; font-size: 16px; flex-shrink: 0; margin-top: 1px; }
  .msg { font-size: 13px; color: #fca5a5; }
`;

const ServerList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const ServerRow = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 12px 14px;
  background: #111;
  border: 1px solid rgba(255,255,255,0.06);
  border-radius: 10px;

  .srv-img {
    width: 46px; height: 46px; border-radius: 8px; overflow: hidden;
    flex-shrink: 0; background: #2A2A2A;
    img { width: 100%; height: 100%; object-fit: cover; display: block; }
  }
  .srv-info { flex: 1; min-width: 0; }
  .srv-name { font-size: 14px; font-weight: 600; color: #e2e8f0; }
  .srv-type { font-size: 11px; color: #6b7280; margin-top: 2px; }
  .srv-badge {
    font-size: 11px; font-weight: 700; padding: 3px 10px; border-radius: 20px;
    flex-shrink: 0;
  }
  .badge-pending {
    background: rgba(251,191,36,0.1); color: #fbbf24;
    border: 1px solid rgba(251,191,36,0.25);
  }
  .badge-active {
    background: rgba(52,211,153,0.1); color: #34d399;
    border: 1px solid rgba(52,211,153,0.25);
  }
`;

const EmptyServers = styled.div`
  text-align: center; padding: 48px 24px; color: #4b5563;
  svg { font-size: 36px; margin-bottom: 12px; opacity: 0.4; }
  .t { font-size: 15px; font-weight: 600; color: #6b7280; }
  .s { font-size: 13px; margin-top: 6px; }
`;

const AddBtn = styled(Link)`
  display: inline-flex; align-items: center; gap: 7px;
  padding: 9px 18px; border-radius: 9px; border: none;
  font-size: 13.5px; font-weight: 600; cursor: pointer;
  background: #7C3AED; color: #fff; text-decoration: none;
  transition: opacity 0.15s; margin-bottom: 16px;
  &:hover { opacity: 0.85; }
`;

const Toast = styled.div`
  position: fixed; bottom: 24px; left: 50%; transform: translateX(-50%);
  background: #166534; border: 1px solid #15803d; border-radius: 10px;
  padding: 12px 24px; color: #bbf7d0; font-size: 13.5px; font-weight: 600;
  z-index: 9999; box-shadow: 0 4px 20px rgba(0,0,0,0.4);
  animation: fadeIn 0.2s ease;
  @keyframes fadeIn { from { opacity: 0; transform: translateX(-50%) translateY(8px); } }
`;

/* ── Component ────────────────────────────── */
const TYPE_LABELS = {
  oldschool: 'Old School', newschool: 'New School',
  middleschool: 'Middle School', custom: 'Custom',
};

const UserProfile = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const auth = useSelector(state => state.auth);
  const users = useSelector(state => state.users);
  const { myServers, myServersLoading } = useSelector(state => state.servers);

  const [tab, setTab] = useState('account');
  const [toast, setToast] = useState('');
  const [editingProfile, setEditingProfile] = useState(false);
  const [imgLoading, setImgLoading] = useState(false);
  const [avatarError, setAvatarError] = useState(false);

  const fileRef = useRef();

  // password form
  const { register, handleSubmit, reset, formState: { errors } } = useForm();

  // profile form
  const {
    register: rp,
    handleSubmit: hsp,
    reset: rsp,
    setValue: svp,
    watch: wp,
    formState: { errors: ep },
  } = useForm();

  useEffect(() => {
    if (tab === 'my_servers') dispatch(getMyServers());
  }, [tab, dispatch]);

  useEffect(() => {
    if (editingProfile) {
      svp('name', auth.user.name);
      svp('gender', auth.user.gender || 'male');
      svp('country', auth.user.country || '');
      const currentAvatar = AVATARS.some(a => a.value === auth.user.avatar) ? auth.user.avatar : `warrior_${auth.user.gender || 'male'}`;
      svp('avatar', currentAvatar);
    }
  }, [editingProfile, auth.user, svp]);

  const userCountry = COUNTRIES.find(c => c.code === auth.user?.country);
  const selectedGenderValue = wp('gender') || auth.user?.gender || 'male';
  const genderAvatars = AVATARS.filter(a => a.value.endsWith(`_${selectedGenderValue}`));
  const selectedAvatarValue = wp('avatar');
  const selectedAvatarObj = AVATARS.find(a => a.value === selectedAvatarValue);

  useEffect(() => {
    if (!editingProfile) return;
    if (selectedAvatarValue && !selectedAvatarValue.endsWith(`_${selectedGenderValue}`)) {
      const currentClass = selectedAvatarValue.replace(/_male$|_female$/, '');
      const sameClass = AVATARS.find(a => a.value === `${currentClass}_${selectedGenderValue}`);
      const next = sameClass || AVATARS.find(a => a.value.endsWith(`_${selectedGenderValue}`));
      if (next) svp('avatar', next.value);
    }
  }, [selectedGenderValue, editingProfile, selectedAvatarValue, svp]);

  if (!auth.isAuth) return <Navigate to="/login" />;

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(''), 2500);
  };

  const onPasswordSubmit = (values) => {
    dispatch(updateUserPassword({ id: auth.user._id, values })).unwrap().then(() => {
      reset();
      showToast(t('website.profile.password_updated'));
    });
  };

  const onProfileSubmit = (values) => {
    dispatch(updateUserProfile({ id: auth.user._id, values })).unwrap().then((res) => {
      dispatch(setUser(res.data || values));
      setEditingProfile(false);
      rsp();
      showToast(t('website.profile.profile_updated'));
    });
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('image', file);
    setImgLoading(true);
    dispatch(updateUserImage({ id: auth.user._id, formData })).unwrap()
      .then((res) => {
        dispatch(setUser(res.data || {}));
        setAvatarError(false);
        showToast(t('website.profile.image_updated'));
      })
      .finally(() => {
        setImgLoading(false);
        fileRef.current.value = '';
      });
  };

  return (
    <WebsiteLayout>
      <Page>
        {/* Header */}
        <Header>
          <AvatarWrap>
            {resolveAvatar(auth.user) && !avatarError ? (
              <AvatarImg gender={auth.user.gender}>
                <img
                  src={resolveAvatar(auth.user)}
                  alt={auth.user.name}
                  onError={() => setAvatarError(true)}
                />
              </AvatarImg>
            ) : (
              <GenderAvatarIcon gender={auth.user.gender}>
                {auth.user.gender === 'female' ? <FiUsers /> : <FiUser />}
              </GenderAvatarIcon>
            )}
            <CameraBtn
              type="button"
              disabled={imgLoading}
              title={t('website.profile.change_photo')}
              onClick={() => fileRef.current.click()}
            >
              <FiCamera />
            </CameraBtn>
            <input ref={fileRef} type="file" accept="image/*" hidden onChange={handleImageUpload} />
          </AvatarWrap>

          <UserMeta>
            <div className="u-name">{auth.user.name}</div>
            <div className="u-role">
              <FiShield />
              {auth.user.role?.name ?? auth.user.type ?? t('website.profile.member')}
            </div>
          </UserMeta>
        </Header>

        {/* Tabs */}
        <Tabs>
          <Tab active={tab === 'account'} onClick={() => setTab('account')}>
            <FiUser /> {t('website.profile.account_info')}
          </Tab>
          <Tab active={tab === 'password'} onClick={() => setTab('password')}>
            <FiLock /> {t('website.profile.change_password')}
          </Tab>
          <Tab active={tab === 'my_servers'} onClick={() => setTab('my_servers')}>
            <FiServer /> {t('website.profile.my_servers')}
          </Tab>
        </Tabs>

        {/* Account tab */}
        {tab === 'account' && (
          <Card>
            <InfoRow>
              <div className="icon"><FiUser /></div>
              <div>
                <div className="label">{t('website.profile.name')}</div>
                <div className="value">{auth.user.name}</div>
              </div>
            </InfoRow>
            <InfoRow>
              <div className="icon"><FiMail /></div>
              <div>
                <div className="label">{t('website.profile.email')}</div>
                <div className="value">{auth.user.email}</div>
              </div>
            </InfoRow>
            <InfoRow>
              <div className="icon"><FiShield /></div>
              <div>
                <div className="label">{t('website.profile.account_type')}</div>
                <div className="value">{auth.user.role?.name ?? auth.user.type ?? t('website.profile.member')}</div>
              </div>
            </InfoRow>
            <InfoRow>
              <div className="icon"><FiUsers /></div>
              <div>
                <div className="label">{t('website.profile.gender')}</div>
                <div className="value">
                  {auth.user.gender === 'female'
                    ? t('website.profile.female')
                    : auth.user.gender === 'male'
                      ? t('website.profile.male')
                      : '—'}
                </div>
              </div>
            </InfoRow>
            <InfoRow>
              <div className="icon">
                {userCountry
                  ? <img src={userCountry.flag} alt={userCountry.name} style={{ width: 20, height: 14, objectFit: 'cover', borderRadius: 2 }} />
                  : <FiFlag />
                }
              </div>
              <div>
                <div className="label">{t('website.profile.country')}</div>
                <div className="value">{userCountry ? userCountry.name : '—'}</div>
              </div>
            </InfoRow>

            {/* Edit profile section */}
            <EditDivider>
              {!editingProfile && (
                <Btn type="button" variant="ghost" onClick={() => setEditingProfile(true)}>
                  <FiEdit2 /> {t('website.profile.edit_profile')}
                </Btn>
              )}
              {editingProfile && <span>{t('website.profile.edit_profile')}</span>}
            </EditDivider>

            {editingProfile && (
              <form onSubmit={hsp(onProfileSubmit)}>
                <FormGroup>
                  <label>{t('website.profile.name')}</label>
                  <input
                    type="text"
                    {...rp('name', { required: t('validation.required') })}
                  />
                  {ep.name && <div className="err">{ep.name.message}</div>}
                </FormGroup>

                <FormGroup>
                  <label>{t('website.profile.gender')}</label>
                  <select {...rp('gender')}>
                    <option value="male">{t('website.profile.male')}</option>
                    <option value="female">{t('website.profile.female')}</option>
                  </select>
                </FormGroup>

                <FormGroup>
                  <label>{t('website.profile.country')}</label>
                  <select {...rp('country')}>
                    <option value="">{t('website.register.country_placeholder')}</option>
                    {COUNTRIES.map(c => (
                      <option key={c.code} value={c.code}>{c.name}</option>
                    ))}
                  </select>
                </FormGroup>

                <FormGroup>
                  <label>{t('website.register.avatar')}</label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    {selectedAvatarObj && (
                      <AvatarImg gender={auth.user.gender} style={{ width: 44, height: 44, flexShrink: 0 }}>
                        <img src={selectedAvatarObj.image} alt={selectedAvatarObj.label} />
                      </AvatarImg>
                    )}
                    <select {...rp('avatar')} style={{ flex: 1 }}>
                      {genderAvatars.map(a => (
                        <option key={a.value} value={a.value}>{a.label}</option>
                      ))}
                    </select>
                  </div>
                </FormGroup>

                <BtnRow>
                  <Btn
                    type="button"
                    variant="reset"
                    disabled={users.isLoading}
                    onClick={() => { setEditingProfile(false); rsp(); }}
                  >
                    <FiRotateCcw /> {t('website.profile.cancel')}
                  </Btn>
                  <Btn type="submit" disabled={users.isLoading}>
                    <FiSave /> {users.isLoading ? t('website.profile.saving') : t('website.profile.save_changes')}
                  </Btn>
                </BtnRow>
              </form>
            )}
          </Card>
        )}

        {/* Password tab */}
        {tab === 'password' && (
          <Card>
            {users.errors.length > 0 && (
              <AlertBox>
                <FiAlertCircle />
                <div className="msg">
                  {users.errors.map((e, i) => <div key={i}>{e?.msg}</div>)}
                </div>
              </AlertBox>
            )}

            <form onSubmit={handleSubmit(onPasswordSubmit)}>
              <FormGroup>
                <label>{t('website.profile.current_password')}</label>
                <input
                  type="password"
                  placeholder={t('website.profile.enter_current_password')}
                  {...register('password', {
                    required: t('validation.required'),
                    minLength: { value: 8, message: t('website.profile.min_8_chars') },
                  })}
                />
                {errors.password && <div className="err">{errors.password.message}</div>}
              </FormGroup>

              <FormGroup>
                <label>{t('website.profile.new_password')}</label>
                <input
                  type="password"
                  placeholder={t('website.profile.enter_new_password')}
                  {...register('new_password', {
                    required: t('validation.required'),
                    minLength: { value: 8, message: t('website.profile.min_8_chars') },
                  })}
                />
                {errors.new_password && <div className="err">{errors.new_password.message}</div>}
              </FormGroup>

              <FormGroup>
                <label>{t('website.profile.confirm_new_password')}</label>
                <input
                  type="password"
                  placeholder={t('website.profile.re_enter_new_password')}
                  {...register('confirm_new_password', {
                    required: t('validation.required'),
                    minLength: { value: 8, message: t('website.profile.min_8_chars') },
                  })}
                />
                {errors.confirm_new_password && (
                  <div className="err">{errors.confirm_new_password.message}</div>
                )}
              </FormGroup>

              <BtnRow>
                <Btn type="reset" variant="reset" onClick={() => reset()} disabled={users.isLoading}>
                  <FiRotateCcw /> {t('website.profile.cancel')}
                </Btn>
                <Btn type="submit" disabled={users.isLoading}>
                  <FiSave /> {users.isLoading ? t('website.profile.saving') : t('website.profile.save_changes')}
                </Btn>
              </BtnRow>
            </form>
          </Card>
        )}

        {/* My Servers tab */}
        {tab === 'my_servers' && (
          <Card>
            <AddBtn to="/add-server">
              <FiPlus /> {t('website.profile.add_server')}
            </AddBtn>

            {myServersLoading ? (
              <div style={{ color: '#6b7280', fontSize: 13, padding: '12px 0' }}>
                {t('website.profile.loading')}
              </div>
            ) : myServers.length === 0 ? (
              <EmptyServers>
                <FiServer />
                <div className="t">{t('website.profile.no_servers')}</div>
                <div className="s">{t('website.profile.no_servers_sub')}</div>
              </EmptyServers>
            ) : (
              <ServerList>
                {myServers.map(srv => (
                  <ServerRow key={srv._id}>
                    <div className="srv-img">
                      <img
                        src={srv.image ? `${backendUrl}/servers/${srv.image}` : noImage}
                        alt={srv.name_ar}
                        onError={e => { e.currentTarget.src = noImage; }}
                      />
                    </div>
                    <div className="srv-info">
                      <div className="srv-name">{srv.name_ar || srv.name_en}</div>
                      <div className="srv-type">{TYPE_LABELS[srv.type] || srv.type}</div>
                    </div>
                    <span className={`srv-badge ${srv.is_active ? 'badge-active' : 'badge-pending'}`}>
                      {srv.is_active
                        ? t('website.profile.status_active')
                        : t('website.profile.status_pending')}
                    </span>
                  </ServerRow>
                ))}
              </ServerList>
            )}
          </Card>
        )}
      </Page>

      {toast && <Toast>{toast}</Toast>}
    </WebsiteLayout>
  );
};

export default UserProfile;
